import { isSentryDeliveryEnabled } from './sentry-delivery-gate';

/**
 * Noise this app should never spend Sentry quota on.
 *
 * `ResizeObserver loop …` is a notification the browser fires when observers
 * reschedule work across frames — benign, and emitted in bursts. Pro Studio
 * filtered it by hand inside its error boundary (manager did not, and showed an
 * error page for it); that filter stays in the boundary, because it does a
 * DIFFERENT job — it stops a benign notification from throwing the user onto an
 * error page. This list is about what reaches Sentry.
 */
export const IGNORED_ERRORS: RegExp[] = [
  // Anchored so it matches the browser's own notification and not a real failure
  // in our code that merely mentions the observer. `(^|:\s)` because the same
  // notification also arrives wrapped — `window.onerror` in some browsers hands
  // it over as "Uncaught Error: ResizeObserver loop limit exceeded", and
  // pro-studio's previous filter (a plain `.includes`) caught that form.
  /(^|:\s)ResizeObserver loop /,
];

export const isIgnoredError = (message: string): boolean =>
  IGNORED_ERRORS.some((pattern) => pattern.test(message));

interface MinimalEvent {
  message?: string;
  exception?: { values?: { type?: string; value?: string }[] };
}

/**
 * Last gate before an event leaves the browser.
 *
 * `ignoreErrors` in `Sentry.init` already covers the common shape; this exists
 * because it also has to hold for events raised through `captureException`
 * directly, and because it is the hook the next filtering rule will attach to.
 */
export const buildBeforeSend =
  () =>
  <T extends MinimalEvent>(event: T): T | null => {
    // Every value, not just the first: `linkedErrorsIntegration` (on by default)
    // expands an error's `cause` chain into `exception.values`, and Sentry's own
    // `ignoreErrors` reads the LAST entry. Reading only `[0]` would make the two
    // gates disagree on exactly the chained errors. The `message` fallback keeps
    // `captureMessage` events — which carry no `exception` at all — filterable.
    const messages = [
      ...(event.exception?.values ?? []).map((value) => value.value ?? ''),
      event.message ?? '',
    ];

    return messages.some(isIgnoredError) ? null : event;
  };

/**
 * The kill switch, as an EVENT PROCESSOR rather than a `beforeSend` hook.
 *
 * `beforeSend` cannot carry it, and neither can `beforeSend` plus
 * `beforeSendTransaction`. `processBeforeSend` in `@sentry/core` dispatches on
 * the event type — `beforeSend` only when `event.type === undefined`,
 * `beforeSendTransaction` only when it is `'transaction'` — so every OTHER type
 * passes both untouched. `Sentry.captureFeedback` builds an event with
 * `type: 'feedback'` (`@sentry/core/build/cjs/feedback.js`), which is exactly
 * what `submitUserBugReport` sends: with the switch on the two hooks, throwing
 * it dropped the bug report's issue and let its feedback card through, leaving
 * a card pointing at an event that does not exist.
 *
 * An event processor runs earlier and for every type: `_processEvent` calls
 * `_prepareEvent` first, and a processor returning `null` there drops the event
 * before `processBeforeSend` is reached (`client.js` — "An event processor
 * returned `null`, will not send event"). So this one function covers errors,
 * transactions, feedback and whatever type a future SDK adds.
 *
 * Note what it still canNOT do, stated rather than implied: session envelopes
 * (`Client.sendSession`) and client reports go straight to the transport and
 * are seen by no processor. Release Health is a separate, cheap quota, so that
 * is accepted rather than fixed here.
 */
export const buildDeliveryGateProcessor =
  () =>
  <T>(event: T): T | null =>
    isSentryDeliveryEnabled() ? event : null;
